import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  baixarPdfDoAcompanhamento,
  ErroDoAcompanhamento,
  obterInfoDoAcompanhamento,
  verificarAcompanhamento,
  type AcompanhamentoDoAssociado,
  type SituacaoDaEtapa,
} from '../../api/acessoAssociadoApi';
import ChecklistSessoesPublicas from '../../components/checklist-publico/ChecklistSessoesPublicas';
import { formatPhoneBr } from '../../utils/phoneMask';
import logoBackground from '../auth/pages/css/img/Atual Clube - Marca_Laranja 1.svg';
import logo from '../auth/pages/css/img/logo.svg';
import '../checklist_publico/ChecklistPublicoPage.css';
import '../../shared/ui/badge-status.css';

/**
 * Acompanhamento do atendimento pelo associado (at-126, ADR 0060). Rota pública
 * `/acompanhamento/:token`, aberta pelo WhatsApp, no celular.
 *
 * Reusa o casco da tela pública do checklist por import (as três telas do associado são a mesma
 * identidade para quem recebe o link) e não tem drawer "Como Funciona": quem lê é o associado, e
 * as explicações vão em hints inline.
 *
 * 🔴 Regra da at-60 em dobro: falha de carga NUNCA vira "nenhuma etapa" nem "nada foi feito
 * ainda" — é a afirmação que chega ao associado. O erro mostra a faixa e nenhum bloco.
 */

type EstadoDoLink = 'verificando' | 'valido' | 'encerrado' | 'desativado' | 'invalido' | 'falhou';

const SELO_DA_ETAPA: Record<SituacaoDaEtapa, { rotulo: string; classe: string }> = {
  PASSOU: { rotulo: 'Passou', classe: 'badge-verde' },
  ATUAL: { rotulo: 'Agora', classe: 'badge-azul' },
  PREVISTA: { rotulo: 'Prevista', classe: 'badge-cinza' },
};

const data = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : null);

const AcompanhamentoPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [estadoDoLink, setEstadoDoLink] = useState<EstadoDoLink>('verificando');
  const [telefone, setTelefone] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [erroVerificacao, setErroVerificacao] = useState('');
  const [erroCarga, setErroCarga] = useState('');
  const [dados, setDados] = useState<AcompanhamentoDoAssociado | null>(null);
  const [telefoneConferido, setTelefoneConferido] = useState('');
  const [baixandoPdf, setBaixandoPdf] = useState(false);
  const [erroPdf, setErroPdf] = useState('');

  useEffect(() => {
    if (!token) {
      setEstadoDoLink('invalido');
      return;
    }
    obterInfoDoAcompanhamento(token)
      .then((info) => setEstadoDoLink(info.desativado ? 'desativado' : info.encerrado ? 'encerrado' : 'valido'))
      .catch((e: ErroDoAcompanhamento) => setEstadoDoLink(e?.status === 404 ? 'invalido' : 'falhou'));
  }, [token]);

  const carregar = async (digitos: string) => {
    if (!token) return;
    setVerificando(true);
    setErroVerificacao('');
    setErroCarga('');
    try {
      const resposta = await verificarAcompanhamento(token, digitos);
      setDados(resposta);
      setTelefoneConferido(digitos);
    } catch (e: any) {
      const status = (e as ErroDoAcompanhamento)?.status;
      if (status === 409) {
        // Dois 409 com leituras diferentes: "encerrado" é deste atendimento (passou o prazo);
        // "desativado" é a oficina ter desligado o acompanhamento, que pode voltar.
        setEstadoDoLink((e as ErroDoAcompanhamento)?.code === 'ACOMPANHAMENTO_DESATIVADO' ? 'desativado' : 'encerrado');
      } else if (status === 401 || status === 400) {
        // A mensagem do servidor é genérica de propósito: não diz de quem é o veículo.
        setErroVerificacao(e?.message || 'O número informado não confere com o cadastro deste veículo.');
      } else {
        setDados(null);
        setErroCarga('Não conseguimos carregar o andamento agora. Tente de novo em instantes.');
      }
    } finally {
      setVerificando(false);
    }
  };

  const handleVerificar = (e: React.FormEvent) => {
    e.preventDefault();
    const digitos = telefone.replace(/\D/g, '');
    if (digitos.length < 10) {
      setErroVerificacao('Informe o celular completo, com DDD.');
      return;
    }
    void carregar(digitos);
  };

  const handleBaixarPdf = async () => {
    if (!token) return;
    setBaixandoPdf(true);
    setErroPdf('');
    try {
      const blob = await baixarPdfDoAcompanhamento(token, telefoneConferido);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `checklist-${dados?.veiculo.plate || 'veiculo'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setErroPdf(e?.message || 'Não foi possível gerar o PDF do checklist agora.');
    } finally {
      setBaixandoPdf(false);
    }
  };

  const nomeDoVeiculo = useMemo(() => {
    if (!dados) return '';
    const marca = dados.veiculo.marca || '';
    const modelo = dados.veiculo.model || '';
    if (marca && modelo.toLowerCase().startsWith(marca.toLowerCase())) return modelo;
    return [marca, modelo].filter(Boolean).join(' ');
  }, [dados]);

  const subtarefasPorEtapa = useMemo(() => {
    const grupos = new Map<string, { nome: string; concluida: boolean }[]>();
    for (const s of dados?.subtarefas ?? []) {
      if (!grupos.has(s.etapa)) grupos.set(s.etapa, []);
      grupos.get(s.etapa)!.push({ nome: s.nome, concluida: s.concluida });
    }
    return Array.from(grupos.entries());
  }, [dados]);

  return (
    <div className="cp-page">
      <div className="logo-background">
        <img className="logo-background-atual" src={logoBackground} alt="logo de fundo" />
      </div>

      <div className="cp-container-wrapper">
        <img className="cp-logo" src={logo} alt="Logo Atual" />

        <div className={`cp-container ${!dados ? 'cp-container--auth' : 'cp-container--full'}`}>
          <div className="cp-header">
            <h1 className="cp-title">Acompanhamento do Veículo</h1>

            {dados && (
              <div className="cp-vehicle-card">
                <div className="cp-vehicle-details">
                  <span className="cp-vehicle-label">Veículo</span>
                  <span className="cp-vehicle-name">{nomeDoVeiculo}</span>
                  {dados.veiculo.proprietarioNome && (
                    <span className="cp-vehicle-owner">Proprietário: {dados.veiculo.proprietarioNome}</span>
                  )}
                  {dados.atendimento.entradaEm && (
                    <span className="cp-vehicle-color">
                      Na oficina desde {data(dados.atendimento.entradaEm)}
                      {dados.atendimento.diasNaOficina !== null && !dados.atendimento.entregueEm
                        ? ` (${dados.atendimento.diasNaOficina} ${dados.atendimento.diasNaOficina === 1 ? 'dia' : 'dias'})`
                        : ''}
                    </span>
                  )}
                </div>
                <div className="cp-plate-badge">
                  <div className="cp-plate-header">
                    <span>BRASIL</span>
                  </div>
                  <div className="cp-plate-number">{dados.veiculo.plate}</div>
                </div>
              </div>
            )}

            {dados?.atendimento.retornoDeGarantia && (
              <p className="cp-hint">
                {dados.atendimento.retornoDeGarantia.origemEntregueEm
                  ? `Retorno de garantia do serviço entregue em ${data(dados.atendimento.retornoDeGarantia.origemEntregueEm)}.`
                  : 'Retorno de garantia de um serviço anterior.'}
              </p>
            )}

            {dados && (
              <p className="cp-hint">
                As informações desta página são só do seu atendimento. Evite encaminhar este link: quem abrir vai
                precisar confirmar o mesmo número de celular cadastrado na oficina.
              </p>
            )}
          </div>

          {estadoDoLink === 'verificando' && (
            <div className="cp-info cp-info--loading">
              <div className="cp-spinner"></div>
              <span>Validando link...</span>
            </div>
          )}

          {estadoDoLink === 'invalido' && (
            <div className="cp-error-box">Link de acompanhamento inválido. Confira se ele foi copiado inteiro.</div>
          )}

          {estadoDoLink === 'encerrado' && (
            <div className="cp-info">
              Este acompanhamento foi encerrado: ele fica disponível até 30 dias depois da entrega do veículo. Para o
              checklist do atendimento, fale com a recepção da oficina.
            </div>
          )}

          {estadoDoLink === 'desativado' && (
            <div className="cp-info">
              O acompanhamento está temporariamente indisponível. Fale com a oficina.
            </div>
          )}

          {estadoDoLink === 'falhou' && (
            <div className="cp-error-box">Não foi possível validar o link agora. Tente de novo em instantes.</div>
          )}

          {estadoDoLink === 'valido' && !dados && (
            <form className="cp-form-auth" onSubmit={handleVerificar}>
              <div className="cp-auth-div">
                <div className="cp-auth-title">
                  <h1 className="cp-h1-auth">Acompanhar o veículo</h1>
                  <p className="cp-p-auth">
                    Confirme o número do celular cadastrado na oficina para ver em que etapa está o seu veículo.
                  </p>
                  <p className="cp-hint">
                    Esta verificação protege as informações do seu veículo: só quem tem o celular cadastrado na oficina
                    consegue abrir. Se o número não for aceito, fale com a oficina para atualizar o cadastro.
                  </p>
                </div>

                <div className="cp-auth-fields">
                  <p className="cp-field-label">Celular</p>
                  <div className="nice-form-group auth">
                    <input
                      type="tel"
                      placeholder="(21) 99999-8888"
                      value={telefone}
                      onChange={(e) => setTelefone(formatPhoneBr(e.target.value))}
                      autoFocus
                      required
                      className="nice-input"
                      aria-label="Celular"
                    />
                  </div>
                </div>

                {erroVerificacao && <div className="cp-error-box">{erroVerificacao}</div>}
                {erroCarga && <div className="cp-error-box">{erroCarga}</div>}

                <div>
                  <button type="submit" disabled={verificando} className="cp-btn-primary">
                    {verificando ? 'Verificando...' : 'Ver andamento'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {dados && (
            <div>
              <section className="cp-session">
                <h2 className="cp-session-title">Etapa atual</h2>
                <div>
                  <div className="cp-item-row">
                    <span className="cp-item-label">
                      <strong>{dados.etapaAtual.nome}</strong>
                    </span>
                    {dados.etapaAtual.desde && (
                      <span className="cp-session-date">desde {data(dados.etapaAtual.desde)}</span>
                    )}
                  </div>
                  {dados.etapaAtual.motivoDaPendencia && (
                    <div className="cp-item-note">
                      <strong>Motivo:</strong> {dados.etapaAtual.motivoDaPendencia}
                    </div>
                  )}
                  {dados.etapaAtual.emParalelo.length > 0 && (
                    <div className="cp-item-note">
                      <strong>Também em andamento:</strong> {dados.etapaAtual.emParalelo.join(', ')}
                    </div>
                  )}
                  {dados.etapaAtual.previsaoDiasTotal !== null && (
                    <>
                      <div className="cp-item-note">
                        <strong>Prazo previsto para esta etapa:</strong> {dados.etapaAtual.previsaoDiasTotal}{' '}
                        {dados.etapaAtual.previsaoDiasTotal === 1 ? 'dia' : 'dias'}
                      </div>
                      <p className="cp-hint">
                        É o prazo total desta etapa, contado a partir do dia em que o veículo entrou nela. Não é o que
                        falta, nem a previsão de entrega do veículo.
                      </p>
                    </>
                  )}
                </div>
              </section>

              <section className="cp-session">
                <h2 className="cp-session-title">Etapas do serviço</h2>
                {dados.etapas.length === 0 ? (
                  <div className="cp-info">As etapas do serviço ainda serão definidas pela oficina.</div>
                ) : (
                  <div>
                    {dados.etapas.map((etapa, i) => (
                      <div key={`${etapa.nome}-${i}`} className="cp-item-row">
                        <span className="cp-item-label">
                          {etapa.nome}
                          {etapa.situacao === 'PASSOU' && etapa.fim ? ` (até ${data(etapa.fim)})` : ''}
                        </span>
                        <span className={`cilia-badge ${SELO_DA_ETAPA[etapa.situacao].classe}`}>
                          {SELO_DA_ETAPA[etapa.situacao].rotulo}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="cp-hint">As etapas previstas podem mudar conforme o serviço avança.</p>
              </section>

              {subtarefasPorEtapa.length > 0 && (
                <section className="cp-session">
                  <h2 className="cp-session-title">O que já foi feito</h2>
                  <div>
                    {subtarefasPorEtapa.map(([etapa, itens]) => (
                      <div key={etapa} className="cp-items-category">
                        <div className="cp-items-category-title">{etapa}</div>
                        {itens.map((item) => (
                          <div key={item.nome} className="cp-item-row">
                            <span className="cp-item-label">{item.nome}</span>
                            <span className={`cilia-badge ${item.concluida ? 'badge-verde' : 'badge-cinza'}`}>
                              {item.concluida ? 'Feito' : 'A fazer'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {dados.oficinaExterna && (
                <section className="cp-session">
                  <h2 className="cp-session-title">Oficina parceira</h2>
                  <div>
                    <div className="cp-item-row">
                      <span className="cp-item-label">
                        <strong>{dados.oficinaExterna.oficina}</strong>
                      </span>
                      <span className="cp-session-date">desde {data(dados.oficinaExterna.entradaEm)}</span>
                    </div>
                    {dados.oficinaExterna.ultimaVisitaEm ? (
                      <div className="cp-item-note">
                        Na última visita do nosso inspetor ({data(dados.oficinaExterna.ultimaVisitaEm)}), a oficina
                        informou
                        {dados.oficinaExterna.etapaInformada ? ` a etapa ${dados.oficinaExterna.etapaInformada}` : ' o andamento'}
                        {dados.oficinaExterna.previsaoDaEtapa
                          ? `, com previsão para esta etapa em ${data(dados.oficinaExterna.previsaoDaEtapa)}`
                          : ''}
                        .
                      </div>
                    ) : (
                      <div className="cp-item-note">O nosso inspetor ainda não registrou visita a esta oficina.</div>
                    )}
                  </div>
                  <p className="cp-hint">
                    Etapa e previsão são o que a oficina parceira informou ao nosso inspetor na visita. Não é um
                    acompanhamento em tempo real.
                  </p>
                </section>
              )}

              <section className="cp-session">
                <h2 className="cp-session-title">Checklist</h2>
                <p className="cp-hint">
                  O checklist de entrada mostra como o veículo chegou, e a saída, como ele foi devolvido. Toque nas
                  fotos para ampliá-las.
                </p>
                <div className="cp-actions">
                  <button type="button" className="cp-btn-download" onClick={handleBaixarPdf} disabled={baixandoPdf}>
                    {baixandoPdf ? 'Gerando PDF...' : 'Baixar PDF do checklist'}
                  </button>
                  {erroPdf && <div className="cp-error-box">{erroPdf}</div>}
                </div>
              </section>
              {/* Sob o título "Checklist", as seções dizem o que cada uma mostra — "Checklist" e
                  "Checklist de entrada" um embaixo do outro liam como coisa repetida. */}
              <ChecklistSessoesPublicas
                entrada={dados.checklist.entrada}
                saida={dados.checklist.saida}
                tituloEntrada="Como o veículo chegou"
                tituloSaida="Como o veículo foi devolvido"
              />

              {dados.pesquisa.disponivel && dados.pesquisa.token && (
                <section className="cp-session">
                  <h2 className="cp-session-title">Como foi o atendimento?</h2>
                  <p className="cp-hint">A pesquisa leva menos de um minuto e é respondida uma vez por atendimento.</p>
                  <div className="cp-actions">
                    <Link className="cp-btn-download" to={`/pesquisa/${dados.pesquisa.token}`}>
                      Responder a pesquisa
                    </Link>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcompanhamentoPage;
